import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Bell, LogOut, Shield, Truck, ClipboardList, KeyRound, Footprints, MessageCircle } from "lucide-react";
import { playSiren, sirenEnabled, isUrgent } from "@/lib/siren";
import SirenToggle from "@/components/SirenToggle";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import PushToggle from "@/components/PushToggle";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import Chat from "@/components/Chat";
import { buildWhatsAppUrl, useRemovalWhatsApp } from "@/components/WhatsAppButton";
import { fmtDateTime } from "@/lib/datetime";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const roleIcon = { admin: Shield, enforcer: ClipboardList, removal: Truck, runner: Footprints };
const roleColor = {
  admin: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  enforcer: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  removal: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  runner: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const removalWa = useRemovalWhatsApp();
  const Icon = user ? roleIcon[user.role] : Shield;

  const loadNotifs = async () => {
    try {
      const res = await api.get("/notifications");
      setNotifs((prev) => {
        if (prev.length && sirenEnabled()) {
          const seen = new Set(prev.map((n) => n.id));
          if (res.data.some((n) => !n.read && !seen.has(n.id) && isUrgent(n.message))) playSiren();
        }
        return res.data;
      });
    } catch (_e) { /* ignore */ }
  };

  usePolling(() => { if (user) loadNotifs(); }, 10000, [user, location.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-[#F8FAFC] grain">
      <header className="sticky top-0 z-40 glass border-b border-[#28364F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="app-logo">
            <div className="hazard-stripes w-2 h-8 rounded-sm" />
            <div>
              <div className="font-display text-lg font-black tracking-tight leading-none">PARKLOCK</div>
              <div className="text-[10px] tracking-widest text-[#94A3B8] uppercase">Field Ops</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {user && (
              <div className={`hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-wider px-2.5 py-1 rounded border ${roleColor[user.role]}`}>
                <Icon className="w-3.5 h-3.5" />
                {user.role}
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold" data-testid="notif-count">
                      {unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 bg-[#131B2A] border-[#28364F] text-white" align="end">
                <DropdownMenuLabel className="flex justify-between items-center">
                  <span>Notifications</span>
                  {unread > 0 && (
                    <button onClick={markAll} className="text-xs text-cyan-400 hover:underline" data-testid="mark-all-read">
                      Mark all read
                    </button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#28364F]" />
                <PushToggle />
                <SirenToggle />
                <DropdownMenuSeparator className="bg-[#28364F]" />
                {notifs.length === 0 && (
                  <div className="px-3 py-6 text-sm text-[#94A3B8] text-center">No notifications yet</div>
                )}
                {notifs.slice(0, 10).map((n) => (
                  <DropdownMenuItem key={n.id} className={`flex-col items-start py-2.5 ${!n.read ? "bg-[#1C263B]" : ""}`}>
                    <div className="text-sm text-white whitespace-normal">{n.message}</div>
                    <div className="text-[10px] text-[#64748B] mt-1">{fmtDateTime(n.created_at)}</div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-sm font-bold">
                    {user?.full_name?.[0] || "?"}
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#131B2A] border-[#28364F] text-white" align="end">
                <DropdownMenuLabel>
                  <div className="text-xs text-[#94A3B8]">Signed in as</div>
                  <div className="text-sm">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#28364F]" />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-btn">
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" className="text-red-300">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 relative z-10">{children}</main>
      {user && (
        <>
          <a
            href={buildWhatsAppUrl({ message: "ParkLock Ops — ", phone: removalWa || "" })}
            target="_blank" rel="noreferrer"
            className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center hover:brightness-110 transition-all"
            data-testid="whatsapp-team-fab" title="Message removal team on WhatsApp">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488" />
            </svg>
          </a>
          <button onClick={() => setChatOpen(true)} className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-cyan-500 text-[#0B0F17] shadow-lg flex items-center justify-center hover:bg-cyan-400 transition-colors" data-testid="team-chat-fab" title="Team chat">
            <MessageCircle className="w-6 h-6" />
          </button>
          <Dialog open={chatOpen} onOpenChange={setChatOpen}>
            <DialogContent className="bg-[#131B2A] border-[#28364F] text-white max-w-md">
              <DialogHeader><DialogTitle>Team Chat</DialogTitle></DialogHeader>
              <Chat scope="team" height="h-80" />
            </DialogContent>
          </Dialog>
        </>
      )}
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
